import {
  Action,
  createAction,
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
} from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import { AddressData, AdminAddressApi, BillApi, ProductApi } from 'api';
import { RootState } from 'app';
import {
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  SummaryBillType,
  ValidateBillConstraintsType,
  convertIntObj,
  ADJUSTMENT_TYPE,
} from 'utils';

export const createReturnBillSliceListenerMiddleware =
  createListenerMiddleware();

export const takeLastedCalcWholesaleProductItemWhenCreateReturnBill =
  createAction<any | undefined>(
    'takeLastedCalcWholesaleProductItemWhenCreateReturnBill'
  );

createReturnBillSliceListenerMiddleware.startListening({
  actionCreator: takeLastedCalcWholesaleProductItemWhenCreateReturnBill,
  effect: async (action: Action, listenerApi: any) => {
    // cancel other running instances
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(800); // delay to wait next action before dispatching final one

    const { payload } = action as any;
    const { id, body } = payload;

    listenerApi.dispatch(
      checkReturnBillItemQuantity({
        id,
        body,
        isUpdatingProductItem: true,
      })
    );
  },
});

export const createReturnBill = createAsyncThunk(
  'returnBill/createReturnBill',
  async (params: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await BillApi.createReturnBill(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkReturnBillItemQuantity = createAsyncThunk(
  'returnBill/checkReturnBillItemQuantity',
  async (
    params: { id: string; body: any; isUpdatingProductItem?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = params;
      const response = await BillApi.checkReturnBillItemQuantity(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductForReturnBill = createAsyncThunk<
  any,
  { body: any },
  { rejectValue: { message: string } }
>(
  'returnBill/findProductForReturnBill',
  async ({ body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductForReturnBill(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForReturnBill = createAsyncThunk(
  'returnBill/createAddressForReturnBill',
  async (param: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = param;
      const response = await AdminAddressApi.create(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAnonymousAddressForReturnBill = createAsyncThunk(
  'returnBill/createAnonymousAddressForReturnBill',
  async (param: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = param;
      const response = await AdminAddressApi.create(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeAddressForReturnBill = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('returnBill/removeAddressForReturnBill', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const updateAddressForReturnBill = createAsyncThunk<
  any,
  { id: string; body: AddressData },
  { rejectValue: { message: string } }
>(
  'returnBill/updateAddressForReturnBill',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await AdminAddressApi.update(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findListAddressForReturnBill = createAsyncThunk(
  'createBill/findListAddressForReturnBill',
  async (param: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = param;
      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface IState {
  selectedProducts: any[];

  summaryBill: SummaryBillType;
  promotions?: any[];
  customerID: string;
  chainCustomerID: string;
  customerOwnerID: string;

  deliveryMethod: ORDER_DELIVERY_METHOD;
  paymentMethod: ORDER_PAYMENT_METHOD;
  address: AddressData | undefined;

  bankAccount: any | undefined;
  locationShippingFee: any | undefined;
  deliveryNote: any | undefined;
  images: any[];
  deliveryFee: number;
  deliveryFeeType: number;

  validationResult: any;

  totalCustomerQuantity: number;

  isProductNotEnoughQuantity?: boolean;
  customerNote: string;
  supplierNote: string;
  customer: any;
  supplier: any;
  cashier: any;
  partnerID: string | undefined;

  addressData: any[];
  billID: string;
  billCode: string;
  bill: any;

  choiceItemList: any[];

  isLoading: boolean;

  totalPrice: number;
  returnMoney: number;

  depositMoney?: {
    cash?: number;
    internetBanking?: number;
    creditCard?: number;
  };

  paidMoney?: {
    cash?: number;
    internetBanking?: number;
    creditCard?: number;
  };

  returnFee?: {
    rate?: number;
    amount?: number;
  };
  shouldUpdateCreateReturnBillData: boolean;
  shouldCreateOrder?: boolean;

  isStock?: boolean;
  deductedMoney?: number;
  excessMoney?: number;
  isDebt?: boolean;
  debtMoney?: number;
  createdAt?: string;
  oldEntityPricePolicyID?: string;
  entityPricePolicyID?: string;
  entityProposalPolicyID?: string;
}

const validationResultInitialState: ValidateBillConstraintsType = {
  isValid: true,
  // deleted proposal items
  isProductItemDeleted: false,
  deletedProductItemIDs: [],
  deletedBillItemIDs: [],
  // out of stock/ not enough proposal items
  isProductItemNotEnoughQuantity: false,
  notEnoughQuantityProductItems: [],
  notEnoughQuantityBillItems: [],
  deactivatedProductIDs: [],
  isProductDeactivated: false,
  // price-changed proposal items
  isProductItemPriceChanged: false,
  priceChangedProductItems: [],
  priceChangedBillItems: [],
  notEnoughQuantityProducts: [],
  isProductPriceChanged: false,
  priceChangedProducts: [],
  isAddressChanged: false,
  changedAddress: undefined,
};

const initialState: IState = {
  selectedProducts: [],

  deliveryMethod: ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY,
  paymentMethod: ORDER_PAYMENT_METHOD.CASH,
  address: undefined,

  bankAccount: undefined,
  locationShippingFee: undefined,
  deliveryNote: undefined,
  images: [],
  deliveryFee: 0,
  deliveryFeeType: 0,

  billID: '',
  billCode: '',
  bill: {},

  customerID: '',
  chainCustomerID: '',
  customerOwnerID: '',

  summaryBill: {
    debtMoney: BigInt(0),
    deductedMoney: BigInt(0),
    deliveryFeeType: 0,
    depositMoney: undefined,
    excessMoney: BigInt(0),
    isDebt: false,
    isValidBillReturnMoney: false,
    paidMoney: undefined,
    returnMoney: BigInt(0),
    totalDepositMoney: BigInt(0),
    totalPaidMoney: BigInt(0),
    deliveryFee: 0,
    totalPrice: BigInt(0),
    totalTemporaryPrice: BigInt(0),
    totalItem: 0,
    totalAdjustment: BigInt(0),
    adjustments: [],
  },
  promotions: [],

  validationResult: {},
  totalCustomerQuantity: 0,
  customerNote: '',
  supplierNote: '',
  customer: {},
  supplier: {},
  cashier: {},
  choiceItemList: [],
  addressData: [],
  isLoading: true,
  partnerID: undefined,
  shouldUpdateCreateReturnBillData: false,
  shouldCreateOrder: false,
  isStock: true,
  returnFee: {},
  totalPrice: 0,
  returnMoney: 0,
  deductedMoney: 0,
  excessMoney: 0,
  isDebt: false,
  debtMoney: 0,
  createdAt: moment().toISOString(),
  oldEntityPricePolicyID: undefined,
  entityPricePolicyID: undefined,
  entityProposalPolicyID: undefined,
};

const initialSummaryBill: SummaryBillType = {
  debtMoney: BigInt(0),
  deductedMoney: BigInt(0),
  deliveryFeeType: 0,
  depositMoney: undefined,
  excessMoney: BigInt(0),
  isDebt: false,
  isValidBillReturnMoney: false,
  paidMoney: undefined,
  returnMoney: BigInt(0),
  totalDepositMoney: BigInt(0),
  totalPaidMoney: BigInt(0),
  deliveryFee: 0,
  totalPrice: BigInt(0),
  totalTemporaryPrice: BigInt(0),
  totalItem: 0,
  totalAdjustment: BigInt(0),
  adjustments: [],
};

const returnBillSlice = createSlice({
  name: 'returnBill',
  initialState,
  reducers: {
    resetCreateReturnBillData(state) {
      state.bankAccount = undefined;
      state.isStock = initialState.isStock;
      state.shouldCreateOrder = initialState.shouldCreateOrder;
    },
    addItemBillToReturn(state, { payload }) {
      handleResetBillData(state);

      const { customerNote, supplierNote, billItems } = payload;

      state.bill = payload;
      state.customerNote = customerNote;
      state.supplierNote = supplierNote;
      state.totalCustomerQuantity = payload.totalCustomerQuantity;
      state.billID = payload.id;
      state.billCode = payload.code;
      state.customer = payload?.customer;
      state.supplier = payload?.supplier;
      state.cashier = payload?.cashier;
      state.customerID = payload?.customer?.id;
      state.chainCustomerID = payload.chainCustomerID;
      state.customerOwnerID = payload?.customer?.ownerID;
      state.partnerID = payload?.partnerID;

      state.selectedProducts = billItems?.map(item => {
        return {
          code: item?.product?.code,
          name: item?.product?.name,
          avatar: item?.product?.avatar,
          billItemID: item?.id,
          currentPrice: item?.price,
          originPrice: item?.originPrice,
          reportType: item?.reportType,
          soldQuantity: item?.product?.soldQuantity,
          customerQuantity: -item?.returnQuantity,
          absoluteQuantity: item?.absoluteQuantity,
          relativeQuantity: item?.relativeQuantity,
          customerNote: item?.customerNote,
          supplierNote: item?.supplierNote,
          priceUnitList: [
            {
              id: 'priceUnit',
              price: item?.price,
              ...item?.supplierProductUnit,
            },
          ],
          priceUnit: {
            id: 'priceUnit',
            price: item?.price,
            ...item?.supplierProductUnit,
          },
          action: 'return',
        };
      });
    },
    addItemForBillToReturn(state, { payload }) {
      state.selectedProducts = [...state.selectedProducts, payload];
    },
    deleteItemForBillToReturn(state, { payload }) {
      const { id } = payload;
      state.selectedProducts = state.selectedProducts?.filter(
        (item: any) => item.id != id
      );
    },
    updateBillInfoToReturn(state, { payload }) {
      const billInfo = payload;

      Object.keys(billInfo).forEach(key => {
        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = billInfo[key] || '';
        }

        if (billInfo[key] != undefined) {
          state[key] = billInfo[key];
        }
      });
    },
    updateCustomerNoteToReturn(state, { payload }) {
      state.customerNote = payload;
    },
    updateDeliveryNoteUpdateBillDataToReturn(state, { payload }) {
      state.deliveryNote = payload.deliveryNote;
    },
    updateSupplierNoteForUpdateBillToReturn(state, { payload }) {
      state.supplierNote = payload;
    },
    updateBankAccountInfoUpdateBillDataToReturn(state, { payload }) {
      if (
        state.bankAccount &&
        payload.bankAccount &&
        payload.bankAccount.id == state.bankAccount.id
      ) {
        state.bankAccount = undefined;
      } else {
        state.bankAccount = payload.bankAccount;
      }
    },
    updateLocationShippingFeeUpdateBillDataToReturn(state, { payload }) {
      if (
        state.locationShippingFee &&
        payload.locationShippingFee &&
        isEqual(state.locationShippingFee, payload.locationShippingFee)
      ) {
        state.locationShippingFee = undefined;
      } else {
        state.locationShippingFee = payload.locationShippingFee;
      }
    },

    updateImagesUpdateBillDataToReturn(state, { payload }) {
      state.images = payload?.images ?? [];
    },
    updatePartnerAddressDataToReturn(state, { payload }) {
      state.addressData = payload;
    },
    updateSelectedItemsToReturn(state, { payload }) {
      const newSelectedProposalItems = payload;
      state.selectedProducts = newSelectedProposalItems;

      if (newSelectedProposalItems.length === 0) {
        state.summaryBill = initialSummaryBill;
      }
    },
    updateShouldUpdateCreateReturnBillData(state, { payload }) {
      state.shouldUpdateCreateReturnBillData = payload;
    },

    handleResetUpdateBillDataToReturn(state) {
      state.selectedProducts = initialState.selectedProducts;
      state.validationResult = initialState.validationResult;
      state.billID = initialState.billID;
      state.billCode = initialState.billCode;
      state.totalCustomerQuantity = initialState.totalCustomerQuantity;
      state.bankAccount = initialState.bankAccount;
    },
    setReloadUpdateBillToReturn(state, { payload }) {
      state.isLoading = payload;
    },
    revertStateWhenHasBeErrorToReturn(state) {
      state.selectedProducts = [...state.selectedProducts];
    },
    resetValidationResultReturnBill: state => {
      state.validationResult = validationResultInitialState;
    },
    setChoiceItemListToReturnBill(state, { payload }) {
      const newSelectedItem = payload;
      state.choiceItemList = [...state.choiceItemList, newSelectedItem];
    },
  },
  extraReducers: builder => {
    builder.addCase(checkReturnBillItemQuantity.fulfilled, (state, action) => {
      const { data } = action.payload;
      const {
        summaryBill,
        billItems,
        validationResult,
        selectedProducts,
        totalQuantity,
      } = data;
      const { isUpdatingProductItem } = action.meta.arg;

      state.shouldUpdateCreateReturnBillData = !isUpdatingProductItem;

      if (data.validationResult && !data.validationResult.isValid) {
        state.shouldUpdateCreateReturnBillData = true;
        state.validationResult = data.validationResult;
        buildItems(state, selectedProducts, billItems);
        updateProductsWithValidationResult(state);
      }

      if (!isUpdatingProductItem) {
        buildItems(state, selectedProducts, billItems);
      }

      state.deliveryFee = summaryBill?.deliveryFee ?? 0;
      state.deliveryFeeType =
        summaryBill?.deliveryFeeDetail?.deliveryFeeType ?? 0;
      state.validationResult = validationResult;
      state.summaryBill = summaryBill;
      state.totalCustomerQuantity = totalQuantity;
      state.promotions = summaryBill.adjustments
        .filter((item: any) => item.type == ADJUSTMENT_TYPE.PROMOTION)
        .map((item: any) => item.info);

      state.totalPrice = Number(summaryBill?.totalPrice ?? 0);
      state.depositMoney = convertIntObj(summaryBill?.depositMoney ?? {});
      state.paidMoney = convertIntObj(summaryBill?.paidMoney ?? {});
      state.returnFee = convertIntObj(summaryBill?.returnFee ?? {});

      state.deductedMoney = Number(summaryBill?.deductedMoney ?? 0);
      state.excessMoney = -Number(summaryBill?.excessMoney ?? 0);
      state.isDebt = summaryBill?.isDebt;
      state.debtMoney = Number(summaryBill?.debtMoney ?? 0);

      if (
        Number(summaryBill?.totalPrice ?? 0) -
          Number(summaryBill?.totalDepositMoney) +
          Number(summaryBill?.totalReturnFee) >=
        0
      ) {
        state.returnMoney = Number(summaryBill?.returnMoney ?? 0);
      } else {
        state.returnMoney =
          summaryBill?.paidMoney &&
          Object.keys(summaryBill?.paidMoney).length > 0
            ? Object.values(summaryBill?.paidMoney)
                .map((item: any) => Number(item))
                .reduce((a: number, b: number) => a + b, 0)
            : 0;
      }
      state.locationShippingFee =
        data?.summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem;
    });
    builder.addCase(findListAddressForReturnBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.addressData = data;
      const selectedAddress = data.find(item => item.isSelected);
      if (selectedAddress) {
        state.address = data.find(item => item.isSelected);
      } else {
        if (data.length > 0) {
          state.address = data[0];
        } else {
          state.address = undefined;
        }
      }
    });
    builder.addCase(createAddressForReturnBill.fulfilled, (state, action) => {
      const { data } = action.payload;

      const address = { ...data, isSupplierCreateForPartner: true };
      if (state.addressData.length === 0) {
        state.address = address;
      }
      state.addressData.push(address);
    });
    builder.addCase(updateAddressForReturnBill.fulfilled, (state, action) => {
      const { data } = action.payload;

      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      state.addressData[foundAddressIndex] = {
        ...data,
        isSupplierCreateForPartner: true,
      };
      if (state.address?.id === data?.id) {
        state.address = data;
      }
    });
    builder.addCase(removeAddressForReturnBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      if (foundAddressIndex !== -1) {
        state.addressData.splice(foundAddressIndex, 1);
      }
    });
  },
});

const updateProductsWithValidationResult = (state: any): void => {
  const {
    deletedProductIDs,
    isPriceChanged,
    priceChangedProducts,
    isAddressChanged,
    changedAddress,
    isBillEntityShippingPolicyItemChanged,
    changedBillEntityShippingPolicyItem,
    deletedProposalItemIDs,
    deletedProductPricePolicyIDs,
    deletedProductProposalPolicyIDs,
  } = state.validationResult;
  if (isAddressChanged && changedAddress) {
    let isSelectedAddress = false;
    const foundAddressIndex = state.addressData.findIndex(
      item => item.id === changedAddress.id
    );

    const changedAddressData = {
      ...changedAddress,
      ward: {
        id: changedAddress.ward?.id,
        code: changedAddress.ward?.code,
        fullName: changedAddress.ward?.fullName,
        district: changedAddress.district,
        province: changedAddress.province,
      },
      district: changedAddress.district,
      province: changedAddress.province,
      isSelected: isSelectedAddress,
    };
    if (foundAddressIndex !== -1) {
      isSelectedAddress = state.addressData[foundAddressIndex].isSelected;
      state.addressData[foundAddressIndex] = changedAddressData;
    }
    state.address = changedAddressData;
  }

  if (deletedProductIDs && deletedProductIDs.length > 0) {
    state.selectedProducts = state.selectedProducts.filter(
      item => !deletedProductIDs.includes(item.id)
    );
  }

  if (
    deletedProductProposalPolicyIDs &&
    deletedProductProposalPolicyIDs.length > 0
  ) {
    state.selectedProducts = state.selectedProducts.filter(
      item => !deletedProductProposalPolicyIDs.includes(item.id)
    );
  }

  if (isPriceChanged) {
    state.selectedProducts.forEach(item => {
      const priceChangedProduct = priceChangedProducts.find(
        priceChangedItem => priceChangedItem.id === item.id
      );

      if (priceChangedProduct) {
        if (
          priceChangedProduct.areAllPriceUnitsDeleted ||
          priceChangedProduct.isOldPriceUnitDeleted ||
          priceChangedProduct.newPriceUnitList?.length
        ) {
          item.priceUnitList = priceChangedProduct.newPriceUnitList.filter(
            priceUnit => priceUnit.isSelected
          );
        } else {
          const indexPriceUnit = item.priceUnitList.findIndex(
            priceUnit => priceUnit.id == priceChangedProduct.newPriceUnit.id
          );
          if (indexPriceUnit == -1) {
            item.priceUnitList.push(priceChangedProduct.newPriceUnit);
          } else {
            item.priceUnitList.splice(
              indexPriceUnit,
              1,
              priceChangedProduct.newPriceUnit
            );
          }
        }

        item.priceUnit = priceChangedProduct.newPriceUnit;
        item.customerQuantity = priceChangedProduct.newCustomerQuantity;
        if (priceChangedProduct.currentPrice) {
          item.originPrice = priceChangedProduct.currentPrice;
        }
        if (priceChangedProduct.originPrice) {
          item.originPrice = priceChangedProduct.originPrice;
        }
        item.productPricePolicyID = priceChangedProduct.productPricePolicyID;
        item.proposalItemID = priceChangedProduct.proposalItemID;
      }
    });
  }

  if (isBillEntityShippingPolicyItemChanged) {
    state.locationShippingFee =
      changedBillEntityShippingPolicyItem.newBillEntityShippingPolicyItem;
  }
};

const buildItems = async (
  state: IState,
  selectedItemList: any[],
  billItems: any[]
): Promise<any> => {
  let selectedProducts = [...state.selectedProducts];

  try {
    selectedProducts = selectedItemList?.map(item => {
      if (item.billItemID) {
        const selectedItem = state?.selectedProducts?.find(
          ProductListItem => ProductListItem.billItemID == item.billItemID
        );

        const selectedBillItem = billItems.find(
          (billItem: any) => billItem.id == item.billItemID
        );

        if (selectedItem) {
          selectedItem.customerQuantity = item.returnQuantity;
          if (selectedBillItem) {
            selectedItem.soldQuantity = selectedItem?.soldQuantity ?? 0;
            selectedItem.currentPrice = selectedBillItem.price;
            selectedItem.originPrice = selectedBillItem.originPrice;
          }

          return selectedItem;
        }

        return item;
      }

      if (item.proposalItemID) {
        const selectedItem = state?.selectedProducts?.find(
          productListItem =>
            productListItem.proposalItemID == item.proposalItemID
        );

        if (selectedItem) {
          selectedItem.billItemID = item.billItemID;
          selectedItem.priceUnit = item.priceUnit;
          selectedItem.priceUnitList = (
            item.priceUnitList ??
            selectedItem.priceUnitList ??
            []
          ).filter(item => item.isSelected);
          selectedItem.reportQuantity = item.reportQuantity;
          selectedItem.customerQuantity = item.customerQuantity;

          selectedItem.currentPrice =
            item.currentPrice ?? item.priceUnit.price ?? 0;
          selectedItem.originPrice =
            item.originPrice ?? item.priceUnit.price ?? 0;
          selectedItem.productPricePolicyID = item.productPricePolicyID;

          return selectedItem;
        }
      }

      if (item.productID) {
        const selectedItem = state?.selectedProducts.find(
          productItem => productItem.productID == item.productID
        );

        if (selectedItem) {
          selectedItem.billItemID = item.billItemID;
          selectedItem.priceUnit = item.priceUnit;
          selectedItem.priceUnitList = (
            item.priceUnitList ?? selectedItem.priceUnitList
          ).filter(item => item.isSelected);
          selectedItem.reportQuantity = item.reportQuantity;
          selectedItem.currentPrice =
            item.currentPrice ?? item.priceUnit.price ?? 0;
          selectedItem.originPrice =
            item.originPrice ?? item.priceUnit.price ?? 0;
          selectedItem.customerQuantity = item.customerQuantity;
          selectedItem.productPricePolicyID = item.productPricePolicyID;

          return selectedItem;
        }
      }

      return item;
    });

    state.selectedProducts = selectedProducts;
  } catch (error: any) {
    console.log(error);
  }
};

const handleResetBillData = (state: any): void => {
  state.chainCustomerID = '';
  state.billID = undefined;
  state.isLoading = true;
  state.selectedProducts = [];
  state.summaryBill = initialSummaryBill;
  state.totalCustomerQuantity = 0;
  state.validationResult = {};
  state.prevCustomerNote = '';
  state.customerNote = '';
  state.supplierNote = '';
  state.customer = {};
  state.supplier = {};

  state.totalPrice = 0;
  state.returnMoney = 0;
  state.paidMoney = {};
  state.depositMoney = {};
  state.returnFee = {};
  state.deductedMoney = 0;
  state.excessMoney = 0;
  state.isDebt = false;
  state.debtMoney = 0;
  state.createdAt = moment().toISOString();
  state.bankAccount = undefined;

  state.isStock = initialState.isStock;
  state.shouldCreateOrder = initialState.shouldCreateOrder;
};

const { actions, reducer: UpdateBillReducer } = returnBillSlice;
export default UpdateBillReducer;
export const {
  addItemBillToReturn,
  updateBillInfoToReturn,
  addItemForBillToReturn,
  deleteItemForBillToReturn,
  updateCustomerNoteToReturn,
  updateSelectedItemsToReturn,
  handleResetUpdateBillDataToReturn,
  updateDeliveryNoteUpdateBillDataToReturn,
  updateBankAccountInfoUpdateBillDataToReturn,
  updateLocationShippingFeeUpdateBillDataToReturn,
  updateImagesUpdateBillDataToReturn,
  updatePartnerAddressDataToReturn,
  updateShouldUpdateCreateReturnBillData,
  setReloadUpdateBillToReturn,
  setChoiceItemListToReturnBill,
  revertStateWhenHasBeErrorToReturn,
  updateSupplierNoteForUpdateBillToReturn,
  resetValidationResultReturnBill,
  resetCreateReturnBillData,
} = actions;

export const selectReturnBill = (state: RootState): IState =>
  state.returnBill as IState;
